/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 添付の複数ファイルアップロードと失敗表示の共通部品。起票フォームと依頼詳細の添付カードで、ファイルを 1 件ずつ順に送り（同じ mutation へ mutate を連打すると TanStack Query v5 は最後の呼び出しのコールバックしか呼ばず、先の失敗が黙って消える）、失敗したファイルを API のメッセージ付きで全件集めて表示する。失敗は Faro にも残す
 * @graph-connects OpsSubmitAttachments [references] 起票後アップロードの順次送信と失敗表示
 * @graph-connects OpsAttachmentsCard [references] 詳細ページの追加アップロードの順次送信と失敗表示
 */
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { logError } from '@cortex/faro';

/** アップロードに失敗した添付 1 件。 @graph-connects none */
export interface FailedUpload {
  /** ファイル名。 */
  name: string;
  /** 失敗理由（API が返したメッセージをそのまま見せる）。 */
  reason: string;
}

/**
 * files を 1 件ずつ順に uploadOne で送り、失敗したものを集めて返す。1 件失敗しても残りは送る。
 * onProgress には送り終えた件数（成否問わず）を渡す。
 * @graph-connects none
 */
export async function uploadFilesSequentially(
  files: readonly File[],
  uploadOne: (file: File) => Promise<unknown>,
  onProgress?: (done: number) => void,
): Promise<FailedUpload[]> {
  const failed: FailedUpload[] = [];
  for (const [index, file] of files.entries()) {
    try {
      await uploadOne(file);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      logError(err, { event: 'ops_attachment_upload_failed' });
      failed.push({ name: file.name, reason: err.message });
    }
    onProgress?.(index + 1);
  }
  return failed;
}

/** OpsFailedUploads の props。 */
export interface OpsFailedUploadsProps {
  /** 失敗した添付（空なら何も出さない）。 */
  failures: readonly FailedUpload[];
  /** 見出し（件数入りの文言は呼び出し側で組み立てる）。 */
  title: string;
  /** 一覧の下に出す導線（任意）。 */
  children?: ReactNode;
  /** 翻訳関数。 */
  t: TFunction;
}

/**
 * 失敗した添付を `ファイル名: 理由` で全件並べる。
 * @graph-connects none
 */
export function OpsFailedUploads({
  failures,
  title,
  children,
  t,
}: OpsFailedUploadsProps): ReactNode {
  if (failures.length === 0) return null;
  return (
    <div role="alert" className="space-y-1 text-xs text-[var(--color-error)]">
      <p className="font-medium">{title}</p>
      <ul className="list-disc space-y-0.5 pl-4">
        {failures.map((failure, index) => (
          <li key={`${failure.name}-${String(index)}`}>
            {t('ops.attachments.errors.failedFile', {
              name: failure.name,
              reason: failure.reason,
            })}
          </li>
        ))}
      </ul>
      {children}
    </div>
  );
}
